import React from 'react';
import { FiCheck, FiX, FiTrash2 } from 'react-icons/fi';

/**
 * Hilfsfunktion um RGB/RGBA-Farben in Hex-Format umzuwandeln
 */
const rgbaToHex = (color) => {
  if (!color || typeof color !== 'string') return '#000000';
  
  // Wenn bereits ein Hex-Wert, gib ihn zurück
  if (color.startsWith('#')) {
    return color;
  }
  
  // RGB/RGBA-Werte extrahieren
  const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
  if (rgbaMatch) {
    const r = parseInt(rgbaMatch[1], 10);
    const g = parseInt(rgbaMatch[2], 10);
    const b = parseInt(rgbaMatch[3], 10);
    
    // In Hex umwandeln
    const toHex = (n) => {
      const hex = n.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }
  
  // Fallback
  return '#000000';
};

/**
 * Wiederverwendbare Komponente für den Bearbeitungsdialog von Titel und Styling-Eigenschaften
 * Wird von CollapsiblePanel und PanelGroup verwendet
 */
export default function EditStyleDialog({
  editTitle,
  setEditTitle,
  editTextColor,
  setEditTextColor,
  editBackgroundColor,
  setEditBackgroundColor,
  editBorderStyle,
  setEditBorderStyle,
  editBorderColor,
  setEditBorderColor,
  editBorderThickness,
  setEditBorderThickness,
  onSave,
  onCancel,
  onDelete,
  titlePlaceholder = "Titel eingeben",
  textColorPlaceholder = "#000000",
  backgroundColorPlaceholder = "#fafafa"
}) {
  return (
    <div 
      style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '16px', 
        padding: '16px', 
        backgroundColor: 'lightgreen', 
        borderRadius: '8px', 
        border: '2px solid rgb(53, 53, 53)',
        minWidth: '300px',

      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <label style={{ minWidth: '100px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>Titel:</label>
        <input
          type="text"
          value={editTitle}
          onChange={e => setEditTitle(e.target.value)}
          placeholder={titlePlaceholder}
          style={{ 
            fontSize: '14px', 
            padding: '8px 12px', 
            flex: 1, 
            minWidth: '200px', 
            borderRadius: '6px', 
            border: '1px solid #ccc',
            outline: 'none',
            transition: 'border-color 0.2s',
          }}
          autoFocus
          onClick={e => e.stopPropagation()}
          onKeyDown={e => {
            if (e.key === 'Enter') onSave();
            if (e.key === 'Escape') onCancel();
          }}
          onFocus={e => e.target.style.borderColor = '#4caf50'}
          onBlur={e => e.target.style.borderColor = '#ccc'}
        />
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <label style={{ minWidth: '100px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>Textfarbe:</label>
        <input
          type="color"
          value={rgbaToHex(editTextColor)}
          onChange={e => setEditTextColor(e.target.value)}
          style={{ 
            width: '50px', 
            height: '36px', 
            border: '1px solid #ccc', 
            borderRadius: '6px', 
            cursor: 'pointer',
            outline: 'none'
          }}
          onClick={e => e.stopPropagation()}
        />
        <input
          type="text"
          value={rgbaToHex(editTextColor)}
          onChange={e => setEditTextColor(e.target.value)}
          style={{ 
            fontSize: '14px', 
            padding: '8px 12px', 
            width: '100px', 
            borderRadius: '6px', 
            border: '1px solid #ccc',
            outline: 'none'
          }}
          onClick={e => e.stopPropagation()}
          placeholder={textColorPlaceholder}
        />
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <label style={{ minWidth: '100px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>Hintergrund:</label>
        <input
          type="color"
          value={rgbaToHex(editBackgroundColor)}
          onChange={e => setEditBackgroundColor(e.target.value)}
          style={{ 
            width: '50px', 
            height: '36px', 
            border: '1px solid #ccc', 
            borderRadius: '6px', 
            cursor: 'pointer',
            outline: 'none'
          }}
          onClick={e => e.stopPropagation()}
        />
        <input
          type="text"
          value={rgbaToHex(editBackgroundColor)}
          onChange={e => setEditBackgroundColor(e.target.value)}
          style={{ 
            fontSize: '14px', 
            padding: '8px 12px', 
            width: '100px', 
            borderRadius: '6px', 
            border: '1px solid #ccc',
            outline: 'none'
          }}
          onClick={e => e.stopPropagation()}
          placeholder={backgroundColorPlaceholder}
        />
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <label style={{ minWidth: '100px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>Rahmen-Stil:</label>
        <select
          value={editBorderStyle}
          onChange={e => setEditBorderStyle(e.target.value)}
          style={{ 
            fontSize: '14px', 
            padding: '8px 12px', 
            borderRadius: '6px', 
            border: '1px solid #ccc',
            outline: 'none',
            cursor: 'pointer',
            backgroundColor: 'white'
          }}
          onClick={e => e.stopPropagation()}
        >
          <option value="solid">Durchgezogen</option>
          <option value="dashed">Gestrichelt</option>
          <option value="dotted">Gepunktet</option>
          <option value="double">Doppelt</option>
          <option value="groove">Eingeprägt</option>
          <option value="ridge">Erhaben</option>
          <option value="inset">Eingelassen</option>
          <option value="outset">Hervorgehoben</option>
          <option value="none">Kein Rahmen</option>
        </select>
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <label style={{ minWidth: '100px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>Rahmen-Farbe:</label>
        <input
          type="color"
          value={rgbaToHex(editBorderColor)}
          onChange={e => setEditBorderColor(e.target.value)}
          style={{ 
            width: '50px', 
            height: '36px', 
            border: '1px solid #ccc', 
            borderRadius: '6px', 
            cursor: 'pointer',
            outline: 'none'
          }}
          onClick={e => e.stopPropagation()}
        />
        <input
          type="text"
          value={rgbaToHex(editBorderColor)}
          onChange={e => setEditBorderColor(e.target.value)}
          style={{ 
            fontSize: '14px', 
            padding: '8px 12px', 
            width: '100px', 
            borderRadius: '6px', 
            border: '1px solid #ccc',
            outline: 'none'
          }}
          onClick={e => e.stopPropagation()}
          placeholder="#ddd"
        />
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <label style={{ minWidth: '100px', fontSize: '14px', fontWeight: 'normal', textAlign: 'left' }}>Rahmen-Dicke:</label>
        <input
          type="range"
          min="0"
          max="10"
          step="1"
          value={editBorderThickness}
          onChange={e => setEditBorderThickness(e.target.value)}
          style={{ 
            flex: 1,
            minWidth: '100px',
            cursor: 'pointer'
          }}
          onClick={e => e.stopPropagation()}
        />
        <input
          type="number"
          min="0"
          max="20"
          value={editBorderThickness}
          onChange={e => setEditBorderThickness(e.target.value)}
          style={{ 
            fontSize: '14px', 
            padding: '8px 12px', 
            width: '60px', 
            borderRadius: '6px', 
            border: '1px solid #ccc',
            outline: 'none'
          }}
          onClick={e => e.stopPropagation()}
        />
        <span style={{ fontSize: '14px', color: '#666' }}>px</span>
      </div>
      
      {/* Buttons Sektion */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px', paddingTop: '16px', borderTop: '1px solid #ddd' }}>
        <button
          style={{ 
            cursor: 'pointer', 
            color: 'white',
            backgroundColor: '#4caf50',
            border: 'none',
            padding: '8px 12px', 
            borderRadius: '6px', 
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onClick={e => {
            e.stopPropagation();
            onSave();
          }}
          onMouseEnter={e => e.target.style.backgroundColor = '#45a049'}
          onMouseLeave={e => e.target.style.backgroundColor = '#4caf50'}
          title="Änderungen speichern"
        >
          <FiCheck />
          Speichern
        </button>
        <button
          style={{ 
            cursor: 'pointer', 
            color: 'white',
            backgroundColor: '#f44336',
            border: 'none',
            padding: '8px 12px', 
            borderRadius: '6px', 
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onClick={e => {
            e.stopPropagation();
            onCancel();
          }}
          onMouseEnter={e => e.target.style.backgroundColor = '#da190b'}
          onMouseLeave={e => e.target.style.backgroundColor = '#f44336'}
          title="Änderungen verwerfen"
        >
          <FiX />
          Abbrechen
        </button>
        {onDelete && (
          <button
            style={{ 
              cursor: 'pointer', 
              color: 'white',
              backgroundColor: '#ff5722',
              border: 'none',
              padding: '8px 12px', 
              borderRadius: '6px', 
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onClick={e => {
              e.stopPropagation();
              onDelete();
            }}
            onMouseEnter={e => e.target.style.backgroundColor = '#d84315'}
            onMouseLeave={e => e.target.style.backgroundColor = '#ff5722'}
            title="Element löschen"
          >
            <FiTrash2 />
            Löschen
          </button>
        )}
      </div>
    </div>
  );
}
